import { useState, useEffect, useRef } from "react";

const VALUES = [100, 200, 300, 400, 500];
const CAT_COLORS = ["#ff3cac", "#00f5ff", "#ffe600", "#3dffa0", "#9b5de5"];
const BUZZ_SEC = 15;

function CircleTimer({ secs, total, size = 80 }) {
  const r = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const pct = total > 0 ? Math.max(0, secs / total) : 0;
  const urgent = secs <= 5;
  const color = urgent ? "#ff3355" : "#00f5ff";
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#ffffff11" strokeWidth={4}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
          strokeLinecap="round" strokeDasharray={`${circ*pct} ${circ}`}
          style={{ transition: "stroke-dasharray 0.95s linear, stroke 0.3s" }}/>
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Fredoka One',sans-serif", fontSize: size * 0.28, color,
        animation: urgent ? "jtpulse 0.5s ease-in-out infinite alternate" : "none",
      }}>{secs}</div>
    </div>
  );
}

export default function JeopardyHost({ board, result, timeout, onReveal, tv }) {
  const [secs, setSecs] = useState(BUZZ_SEC);
  const timerRef = useRef(null);
  const prevPhase = useRef(null);

  useEffect(() => {
    const phase = board?.phase;
    if (phase === "question" && prevPhase.current !== "question") {
      clearInterval(timerRef.current);
      setSecs(BUZZ_SEC);
      timerRef.current = setInterval(() =>
        setSecs(s => { if (s <= 1) { clearInterval(timerRef.current); return 0; } return s - 1; })
      , 1000);
    } else if (phase !== "question") {
      clearInterval(timerRef.current);
    }
    prevPhase.current = phase;
  }, [board?.phase, board?.current?.question]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  if (!board) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:"3rem", color:"#aaa" }}>
      <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ffe600", fontSize:"1.5rem" }}>🏆 Своя игра</div>
      <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:"1rem" }}>Загружаем доску...</div>
    </div>
  );

  const { categories, opened, scores, phase, current, buzzed_by, cant_buzz, wrong_answers, turn_player } = board;
  const openedSet = new Set(opened || []);
  const fs = tv ? "1rem" : "0.85rem";
  const activeResult = timeout ? { ...timeout, timeout: true } : result;

  if (activeResult) {
    const isTimeout = Boolean(activeResult.timeout);
    const correct = activeResult.correct === true;
    return (
      <div style={{display:"flex", alignItems:"center", justifyContent:"center", minHeight:tv ? 420 : 300, padding:"1rem"}}>
        <div style={{
          background:isTimeout ? "#1a1a2b" : correct ? "#0d2b1a" : "#2b0d0d",
          border:`3px solid ${isTimeout ? "#ffe600" : correct ? "#3dffa0" : "#ff3355"}`,
          borderRadius:"1.5rem", boxShadow:"6px 6px 0 #000",
          padding:tv ? "2rem 3rem" : "1.25rem 1.75rem", textAlign:"center", maxWidth:650, width:"100%",
        }}>
          <div style={{fontSize:tv ? "4rem" : "2.8rem", marginBottom:8}}>
            {isTimeout ? (activeResult.revealed ? "👁" : "⏱") : (correct ? "✅" : "❌")}
          </div>
          <div style={{fontFamily:"'Fredoka One',sans-serif", color:isTimeout ? "#ffe600" : correct ? "#3dffa0" : "#ff3355", fontSize:tv ? "2rem" : "1.35rem"}}>
            {isTimeout
              ? (activeResult.revealed ? "Ответ раскрыт" : "Время вышло")
              : correct
                ? `${activeResult.name} ответил верно! +${activeResult.value}`
                : `${activeResult.name} — неверно −${activeResult.value}`}
          </div>
          {activeResult.player_answer && (
            <div style={{fontFamily:"'Nunito',sans-serif", color:"#aaa", marginTop:8}}>
              Ответ: «{activeResult.player_answer}»
            </div>
          )}
          {activeResult.answer && (
            <div style={{fontFamily:"'Nunito',sans-serif", color:"#aaa", marginTop:8}}>
              Правильный ответ: <strong style={{color:"#fff"}}>{activeResult.answer}</strong>
            </div>
          )}
          {activeResult.remaining > 0 && (
            <div style={{fontFamily:"'Nunito',sans-serif", color:"#777", marginTop:8}}>
              Ещё могут ответить: {activeResult.remaining}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Вопрос / ввод ─────────────────────────────────────────────────────────
  if (phase === "question" || phase === "typing") {
    return (
      <div style={{ width:"100%", padding: tv ? "1.5rem" : "0.75rem" }}>
        <style>{`@keyframes jtpulse{from{transform:scale(1)}to{transform:scale(1.2)}}`}</style>

        {/* Вопрос */}
        <div style={{
          background:"#1e0845", border:"3px solid #000", borderRadius:"1.5rem",
          boxShadow:"5px 5px 0 #000", padding: tv ? "2rem 2.5rem" : "1rem 1.5rem",
          textAlign:"center", marginBottom:"1.25rem",
        }}>
          <div style={{ fontFamily:"'Nunito',sans-serif", color:"#ffe600", fontSize: tv ? "1rem" : "0.8rem", marginBottom:8 }}>
            🏆 {categories?.[current?.cat_idx]?.category} — {current?.value} очков
          </div>
          <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#fff", fontSize: tv ? "2.2rem" : "1.4rem", lineHeight:1.3 }}>
            {current?.question}
          </div>
        </div>

        {/* Фаза ожидания buzz */}
        {phase === "question" && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
            <CircleTimer secs={secs} total={BUZZ_SEC} size={tv ? 120 : 80}/>

            {/* Неверные ответы */}
            {Object.keys(wrong_answers || {}).length > 0 && (
              <div style={{ background:"#2b0d0d", border:"2px solid #ff335533", borderRadius:"0.75rem", padding:"0.5rem 1rem", textAlign:"center" }}>
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#ff3355", fontSize:fs, marginBottom:4 }}>Неверные ответы:</div>
                {Object.entries(wrong_answers).map(([n, a]) => (
                  <div key={n} style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:fs }}>
                    <strong style={{color:"#fff"}}>{n}</strong>: «{a}»
                  </div>
                ))}
              </div>
            )}

            {cant_buzz?.length > 0 && Object.keys(wrong_answers || {}).length === 0 && (
              <div style={{ fontFamily:"'Nunito',sans-serif", color:"#555", fontSize:fs }}>
                Ответили неверно: {cant_buzz.join(", ")}
              </div>
            )}

            <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#00f5ff", fontSize: tv ? "1.2rem" : "0.95rem" }}>
              ⏳ Ждём кто нажмёт кнопку...
            </div>

            <button onClick={onReveal} style={{
              background:"transparent", border:"1.5px solid #ffffff22", borderRadius:"0.75rem",
              padding:"0.35rem 1rem", color:"#555", fontFamily:"'Nunito',sans-serif",
              fontWeight:700, fontSize:fs, cursor:"pointer", transition:"all 0.15s",
            }}
            onMouseEnter={e=>{e.currentTarget.style.color="#aaa";e.currentTarget.style.borderColor="#ffffff44";}}
            onMouseLeave={e=>{e.currentTarget.style.color="#555";e.currentTarget.style.borderColor="#ffffff22";}}
            >👁 Раскрыть ответ (0 очков)</button>
          </div>
        )}

        {/* Фаза ввода */}
        {phase === "typing" && (
          <div style={{ textAlign:"center" }}>
            <div style={{
              display:"inline-block", background:"#ff3cac22", border:"3px solid #ff3cac",
              borderRadius:"1.25rem", boxShadow:"4px 4px 0 #000",
              padding: tv ? "1rem 2rem" : "0.6rem 1.5rem", marginBottom:"0.75rem",
            }}>
              <div style={{ fontFamily:"'Fredoka One',sans-serif", color:"#ff3cac", fontSize: tv ? "1.8rem" : "1.2rem" }}>
                ✍️ {buzzed_by} вводит ответ...
              </div>
            </div>

            {Object.keys(wrong_answers || {}).length > 0 && (
              <div style={{ background:"#2b0d0d", border:"2px solid #ff335533", borderRadius:"0.75rem", padding:"0.5rem 1rem", marginBottom:"0.75rem", display:"inline-block" }}>
                <div style={{ fontFamily:"'Nunito',sans-serif", color:"#ff3355", fontSize:fs, marginBottom:4 }}>Уже ответили неверно:</div>
                {Object.entries(wrong_answers).map(([n, a]) => (
                  <div key={n} style={{ fontFamily:"'Nunito',sans-serif", color:"#aaa", fontSize:fs }}>
                    <strong style={{color:"#fff"}}>{n}</strong>: «{a}»
                  </div>
                ))}
              </div>
            )}

            <div>
              <button onClick={onReveal} style={{
                background:"transparent", border:"1.5px solid #ffffff22", borderRadius:"0.75rem",
                padding:"0.35rem 1rem", color:"#555", fontFamily:"'Nunito',sans-serif",
                fontWeight:700, fontSize:fs, cursor:"pointer", transition:"all 0.15s",
              }}
              onMouseEnter={e=>{e.currentTarget.style.color="#aaa";e.currentTarget.style.borderColor="#ffffff44";}}
              onMouseLeave={e=>{e.currentTarget.style.color="#555";e.currentTarget.style.borderColor="#ffffff22";}}
              >👁 Раскрыть ответ (0 очков)</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Доска ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ width:"100%", overflowX:"auto" }}>
      {/* Очки */}
      <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:"0.75rem", flexWrap:"wrap" }}>
        {Object.entries(scores || {}).sort((a,b)=>b[1]-a[1]).map(([name, sc]) => (
          <div key={name} style={{
            background: name===turn_player ? "#3dffa022" : "#1e0845",
            border: `2px solid ${name===turn_player ? "#3dffa0" : "#ffffff22"}`,
            borderRadius:"0.75rem", padding:"0.3rem 0.75rem",
            fontFamily:"'Fredoka One',sans-serif", fontSize:fs, color:"#fff",
          }}>
            {name===turn_player ? "✨ " : ""}{name} <span style={{color:"#ffe600"}}>{sc}</span>
          </div>
        ))}
      </div>
      <div style={{ textAlign:"center", marginBottom:"0.75rem", fontFamily:"'Nunito',sans-serif", fontSize:fs }}>
        {turn_player
          ? <span style={{color:"#3dffa0"}}>✨ Выбирает: <strong>{turn_player}</strong></span>
          : <span style={{color:"#555"}}>Ожидание...</span>}
      </div>
      <table style={{ width:"100%", borderCollapse:"separate", borderSpacing:6 }}>
        <thead>
          <tr>{categories.map((cat, i) => (
            <th key={i} style={{
              background: CAT_COLORS[i%CAT_COLORS.length]+"22",
              border: `2px solid ${CAT_COLORS[i%CAT_COLORS.length]}55`,
              borderRadius:"0.75rem", padding: tv ? "0.6rem 0.4rem" : "0.4rem 0.25rem",
              fontFamily:"'Fredoka One',sans-serif", fontSize:fs,
              color: CAT_COLORS[i%CAT_COLORS.length], textAlign:"center",
            }}>{cat.category}</th>
          ))}</tr>
        </thead>
        <tbody>
          {VALUES.map(val => (
            <tr key={val}>{categories.map((cat, ci) => {
              const isOpen = openedSet.has(`${ci}:${val}`);
              return (
                <td key={ci} style={{
                  background: isOpen ? "#0a0a14" : "#1e0845",
                  border: isOpen ? "2px solid #ffffff0a" : `2px solid ${CAT_COLORS[ci%CAT_COLORS.length]}44`,
                  borderRadius:"0.75rem", textAlign:"center",
                  padding: tv ? "1rem 0.5rem" : "0.6rem 0.3rem",
                }}>
                  <span style={{
                    fontFamily:"'Fredoka One',sans-serif", fontSize: tv ? "1.5rem" : "1.1rem",
                    color: isOpen ? "#333" : "#ffe600",
                    textShadow: isOpen ? "none" : "2px 2px 0 #000",
                  }}>{isOpen ? "✓" : val}</span>
                </td>
              );
            })}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}